import React, { useState, useRef, useEffect } from 'react';
import { FaEnvelope, FaPaperPlane, FaMapMarkerAlt, FaPhone, FaGithub, FaLinkedin, FaTwitter } from 'react-icons/fa';
import axios from 'axios';

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState('');
  const [sent, setSent] = useState(false);
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const particlesRef = useRef([]);

  // Scroll-in refs
  const headerRef  = useRef(null);
  const availRef   = useRef(null);
  const card0Ref   = useRef(null);
  const card1Ref   = useRef(null);
  const card2Ref   = useRef(null);
  const socialsRef = useRef(null);
  const formRef    = useRef(null);

  // IntersectionObserver scroll-in
  useEffect(() => {
    const targets = [
      { el: headerRef.current,  cls: 'ct-reveal-up',   delay: 0   },
      { el: availRef.current,   cls: 'ct-reveal-left',  delay: 80  },
      { el: card0Ref.current,   cls: 'ct-reveal-left',  delay: 160 },
      { el: card1Ref.current,   cls: 'ct-reveal-left',  delay: 240 },
      { el: card2Ref.current,   cls: 'ct-reveal-left',  delay: 320 },
      { el: socialsRef.current, cls: 'ct-reveal-up',   delay: 400 },
      { el: formRef.current,    cls: 'ct-reveal-right', delay: 120 },
    ];

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const found = targets.find(t => t.el === entry.target);
          if (found) {
            setTimeout(() => {
              entry.target.classList.add('ct-visible');
            }, found.delay);
          }
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });

    targets.forEach(({ el }) => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  // Particle grid background
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let width, height;

    const resize = () => {
      width = canvas.width = canvas.offsetWidth;
      height = canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Create dot grid particles
    particlesRef.current = [];
    const cols = 28, rows = 18;
    for (let i = 0; i < cols; i++) {
      for (let j = 0; j < rows; j++) {
        particlesRef.current.push({
          bx: (i / (cols - 1)),
          by: (j / (rows - 1)),
          phase: Math.random() * Math.PI * 2,
          speed: 0.3 + Math.random() * 0.4,
          size: 1 + Math.random() * 1.2,
        });
      }
    }

    let t = 0;
    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      t += 0.008;
      particlesRef.current.forEach(p => {
        const x = p.bx * width;
        const y = p.by * height;
        const wave = Math.sin(t * p.speed + p.phase + p.bx * 4 + p.by * 3);
        const alpha = 0.08 + 0.18 * ((wave + 1) / 2);
        const size = p.size + 0.5 * wave;
        ctx.beginPath();
        ctx.arc(x, y, Math.max(0.5, size), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0,245,212,${alpha})`;
        ctx.fill();
      });
      animRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post('http://localhost:5000/api/contact', formData);
      setSent(true);
      setStatus({ type: 'success', message: 'Message sent! I\'ll reply soon.' });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch {
      setStatus({ type: 'error', message: 'Failed to send. Try emailing directly.' });
    } finally {
      setLoading(false);
      setTimeout(() => setStatus({ type: '', message: '' }), 5000);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=Space+Mono:wght@400;700&display=swap');

        :root {
          --a:  #00f5d4;
          --a2: #7b61ff;
          --a3: #ff4ecd;
          --bg: #07090f;
          --tx: #e8eaf6;
          --mu: rgba(232,234,246,0.4);
          --bd: rgba(0,245,212,0.12);
        }

        /* ── SCROLL-IN ANIMATIONS ── */
        .ct-reveal-up,
        .ct-reveal-left,
        .ct-reveal-right {
          opacity: 0;
          transition: opacity 0.7s cubic-bezier(0.22,1,0.36,1),
                      transform 0.7s cubic-bezier(0.34,1.3,0.64,1),
                      filter 0.6s ease;
          will-change: opacity, transform;
        }
        .ct-reveal-up    { transform: translateY(40px); filter: blur(4px); }
        .ct-reveal-left  { transform: translateX(-36px); filter: blur(4px); }
        .ct-reveal-right { transform: translateX(36px); filter: blur(4px); }

        .ct-reveal-up.ct-visible,
        .ct-reveal-left.ct-visible,
        .ct-reveal-right.ct-visible {
          opacity: 1;
          transform: translate(0, 0);
          filter: blur(0);
        }

        /* ── SECTION ── */
        .ct-section {
          position: relative;
          padding: 7rem 2rem 6rem;
          background: var(--bg);
          font-family: 'Syne', sans-serif;
          overflow: hidden;
        }

        /* Ambient blobs */
        .ct-blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          pointer-events: none;
          z-index: 0;
        }
        .ct-blob-1 {
          width: 500px; height: 500px;
          background: radial-gradient(circle, rgba(123,97,255,0.12), transparent 70%);
          top: -100px; right: -100px;
        }
        .ct-blob-2 {
          width: 400px; height: 400px;
          background: radial-gradient(circle, rgba(0,245,212,0.08), transparent 70%);
          bottom: -80px; left: -80px;
        }
        .ct-blob-3 {
          width: 300px; height: 300px;
          background: radial-gradient(circle, rgba(255,78,205,0.06), transparent 70%);
          top: 50%; left: 40%;
        }

        /* Dot canvas */
        .ct-canvas {
          position: absolute;
          inset: 0;
          width: 100%; height: 100%;
          z-index: 0;
          pointer-events: none;
          opacity: 0.6;
        }

        .ct-inner {
          position: relative;
          z-index: 1;
          max-width: 1200px;
          margin: 0 auto;
        }

        /* ── HEADER ── */
        .ct-header {
          text-align: center;
          margin-bottom: 5rem;
        }
        .ct-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          font-family: 'Space Mono', monospace;
          font-size: 0.65rem;
          letter-spacing: 0.25em;
          text-transform: uppercase;
          color: var(--a);
          margin-bottom: 1.2rem;
        }
        .ct-eyebrow-line {
          display: block;
          width: 32px; height: 1px;
          background: linear-gradient(90deg, transparent, var(--a));
        }
        .ct-eyebrow-line.right { background: linear-gradient(90deg, var(--a), transparent); }
        .ct-title {
          font-size: clamp(2rem, 5vw, 3.5rem);
          font-weight: 800;
          letter-spacing: -0.04em;
          color: #fff;
          line-height: 1.05;
        }
        .ct-title-grad {
          background: linear-gradient(135deg, var(--a) 0%, var(--a2) 50%, var(--a3) 100%);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          background-size: 200% auto;
          animation: gradShift 5s linear infinite;
        }
        @keyframes gradShift {
          0%   { background-position: 0% center; }
          100% { background-position: 200% center; }
        }
        .ct-subtitle {
          margin-top: 1rem;
          font-size: 0.95rem;
          color: var(--mu);
          line-height: 1.7;
          max-width: 480px;
          margin-inline: auto;
        }

        /* ── GRID ── */
        .ct-grid {
          display: grid;
          grid-template-columns: 380px 1fr;
          gap: 2.5rem;
          align-items: start;
        }

        /* ── LEFT PANEL ── */
        .ct-left {
          display: flex;
          flex-direction: column;
          gap: 1.2rem;
        }

        /* Availability badge */
        .ct-avail {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: rgba(0,245,212,0.06);
          border: 1px solid rgba(0,245,212,0.2);
          border-radius: 100px;
          font-family: 'Space Mono', monospace;
          font-size: 0.68rem;
          letter-spacing: 0.1em;
          color: var(--a);
          width: fit-content;
        }
        .ct-avail-dot {
          width: 7px; height: 7px;
          border-radius: 50%;
          background: var(--a);
          box-shadow: 0 0 8px var(--a);
          animation: avPulse 1.8s ease-in-out infinite;
        }
        @keyframes avPulse {
          0%,100% { transform: scale(1); opacity:1; }
          50% { transform: scale(1.5); opacity:0.6; }
        }

        /* Info cards */
        .ct-info-card {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem 1.2rem;
          background: rgba(255,255,255,0.025);
          border: 1px solid var(--bd);
          border-radius: 14px;
          transition: border-color 0.3s, background 0.3s, transform 0.3s cubic-bezier(0.34,1.56,0.64,1);
          cursor: default;
        }
        .ct-info-card:hover {
          border-color: rgba(0,245,212,0.3);
          background: rgba(0,245,212,0.04);
          transform: translateX(6px);
        }
        .ct-icard-icon {
          width: 42px; height: 42px;
          border-radius: 11px;
          background: rgba(0,245,212,0.07);
          border: 1px solid rgba(0,245,212,0.15);
          display: flex; align-items: center; justify-content: center;
          color: var(--a);
          font-size: 1.1rem;
          flex-shrink: 0;
          transition: background 0.3s, box-shadow 0.3s;
        }
        .ct-info-card:hover .ct-icard-icon {
          background: rgba(0,245,212,0.12);
          box-shadow: 0 0 16px rgba(0,245,212,0.25);
        }
        .ct-icard-label {
          font-family: 'Space Mono', monospace;
          font-size: 0.62rem;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--mu);
          margin-bottom: 3px;
        }
        .ct-icard-val {
          font-size: 0.88rem;
          color: var(--tx);
          font-weight: 600;
          letter-spacing: -0.01em;
        }
        .ct-icard-val a { color: inherit; text-decoration: none; transition: color 0.2s; }
        .ct-icard-val a:hover { color: var(--a); }

        /* Socials */
        .ct-socials-row {
          display: flex;
          gap: 10px;
          margin-top: 4px;
        }
        .ct-soc {
          width: 40px; height: 40px;
          border-radius: 10px;
          border: 1px solid var(--bd);
          background: rgba(255,255,255,0.025);
          display: flex; align-items: center; justify-content: center;
          color: var(--mu);
          font-size: 1.1rem;
          text-decoration: none;
          transition: color 0.25s, border-color 0.25s, background 0.25s,
                      transform 0.3s cubic-bezier(0.34,1.56,0.64,1),
                      box-shadow 0.3s;
        }
        .ct-soc:hover {
          color: var(--a);
          border-color: rgba(0,245,212,0.35);
          background: rgba(0,245,212,0.07);
          transform: translateY(-4px) rotate(-5deg);
          box-shadow: 0 8px 20px rgba(0,245,212,0.18);
        }
        .ct-soc:nth-child(2):hover { transform: translateY(-4px) scale(1.12); }
        .ct-soc:nth-child(3):hover { transform: translateY(-4px) rotate(5deg); }

        /* ── FORM CARD ── */
        .ct-form-card {
          position: relative;
          border-radius: 24px;
          overflow: hidden;
        }

        /* Animated border using gradient */
        .ct-form-card::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 24px;
          padding: 1.5px;
          background: linear-gradient(
            135deg,
            rgba(0,245,212,0.4) 0%,
            rgba(123,97,255,0.3) 30%,
            rgba(255,78,205,0.2) 60%,
            rgba(0,245,212,0.3) 100%
          );
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          background-size: 300% 300%;
          animation: borderFlow 6s linear infinite;
        }
        @keyframes borderFlow {
          0%   { background-position: 0% 0%; }
          50%  { background-position: 100% 100%; }
          100% { background-position: 0% 0%; }
        }

        .ct-form-inner {
          position: relative;
          background: rgba(8,11,20,0.92);
          backdrop-filter: blur(20px);
          border-radius: 24px;
          padding: 2.5rem;
          z-index: 1;
        }

        /* Corner accents */
        .ct-corner {
          position: absolute;
          width: 16px; height: 16px;
          pointer-events: none;
          z-index: 2;
        }
        .ct-corner::before, .ct-corner::after {
          content: '';
          position: absolute;
          background: var(--a);
        }
        .ct-corner::before { width: 16px; height: 1.5px; }
        .ct-corner::after  { width: 1.5px; height: 16px; }
        .ct-corner-tl { top: 12px; left: 12px; }
        .ct-corner-tr { top: 12px; right: 12px; transform: scaleX(-1); }
        .ct-corner-bl { bottom: 12px; left: 12px; transform: scaleY(-1); }
        .ct-corner-br { bottom: 12px; right: 12px; transform: scale(-1); }

        /* Form header */
        .ct-form-title {
          font-size: 1.05rem;
          font-weight: 700;
          color: var(--tx);
          margin-bottom: 0.25rem;
          letter-spacing: -0.01em;
        }
        .ct-form-desc {
          font-family: 'Space Mono', monospace;
          font-size: 0.65rem;
          color: var(--mu);
          letter-spacing: 0.06em;
          margin-bottom: 2rem;
        }

        /* Field row */
        .ct-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }

        /* Field group */
        .ct-field {
          position: relative;
          margin-bottom: 1.1rem;
        }

        /* Floating label */
        .ct-label {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          font-family: 'Space Mono', monospace;
          font-size: 0.7rem;
          letter-spacing: 0.06em;
          color: var(--mu);
          pointer-events: none;
          transition: all 0.25s cubic-bezier(0.34,1.2,0.64,1);
          z-index: 2;
        }
        .ct-field.textarea .ct-label {
          top: 18px;
          transform: none;
        }
        .ct-field.active .ct-label,
        .ct-field.filled .ct-label {
          top: -9px;
          transform: none;
          font-size: 0.6rem;
          color: var(--a);
          background: rgba(8,11,20,1);
          padding: 0 6px;
          letter-spacing: 0.12em;
        }
        .ct-field.textarea.active .ct-label,
        .ct-field.textarea.filled .ct-label {
          top: -9px;
        }

        /* Input & Textarea */
        .ct-input, .ct-textarea {
          width: 100%;
          box-sizing: border-box;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px;
          padding: 14px 14px;
          font-family: 'Syne', sans-serif;
          font-size: 0.88rem;
          color: var(--tx);
          transition: border-color 0.25s, background 0.25s, box-shadow 0.25s;
          position: relative;
          z-index: 1;
        }
        .ct-input:focus, .ct-textarea:focus {
          outline: none;
          border-color: var(--a);
          background: rgba(0,245,212,0.04);
          box-shadow: 0 0 0 3px rgba(0,245,212,0.08), 0 0 20px rgba(0,245,212,0.06);
        }
        .ct-input::placeholder, .ct-textarea::placeholder { color: transparent; }
        .ct-textarea {
          resize: none;
          min-height: 110px;
        }

        /* Focus line decoration */
        .ct-field-line {
          position: absolute;
          bottom: 0; left: 12px; right: 12px;
          height: 1.5px;
          background: linear-gradient(90deg, var(--a), var(--a2));
          border-radius: 2px;
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.35s cubic-bezier(0.34,1.56,0.64,1);
          z-index: 3;
          pointer-events: none;
        }
        .ct-field.active .ct-field-line { transform: scaleX(1); }

        /* Character counter */
        .ct-counter {
          position: absolute;
          bottom: 10px; right: 12px;
          font-family: 'Space Mono', monospace;
          font-size: 0.58rem;
          color: var(--mu);
          z-index: 2;
          pointer-events: none;
          transition: color 0.2s;
        }
        .ct-counter.warn { color: var(--a3); }

        /* Alert */
        .ct-alert {
          padding: 12px 16px;
          border-radius: 10px;
          font-family: 'Space Mono', monospace;
          font-size: 0.72rem;
          letter-spacing: 0.04em;
          margin-bottom: 1.25rem;
          display: flex;
          align-items: center;
          gap: 8px;
          animation: fadeUp 0.3s ease;
        }
        .ct-alert.success {
          background: rgba(0,245,212,0.08);
          border: 1px solid rgba(0,245,212,0.3);
          color: var(--a);
        }
        .ct-alert.error {
          background: rgba(255,78,205,0.07);
          border: 1px solid rgba(255,78,205,0.25);
          color: var(--a3);
        }

        /* Submit button */
        .ct-btn {
          position: relative;
          width: 100%;
          height: 52px;
          border: none;
          border-radius: 14px;
          background: linear-gradient(135deg, var(--a) 0%, #00c9a8 40%, var(--a2) 100%);
          background-size: 200% 200%;
          color: #07090f;
          font-family: 'Space Mono', monospace;
          font-size: 0.78rem;
          font-weight: 700;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          cursor: pointer;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: transform 0.25s cubic-bezier(0.34,1.56,0.64,1),
                      box-shadow 0.25s ease,
                      background-position 0.4s ease;
          margin-top: 0.5rem;
        }
        .ct-btn:hover:not(:disabled) {
          transform: translateY(-3px) scale(1.01);
          box-shadow: 0 12px 40px -8px rgba(0,245,212,0.55),
                      0 4px 16px rgba(0,0,0,0.4);
          background-position: right center;
        }
        .ct-btn:active:not(:disabled) { transform: translateY(0) scale(0.98); }
        .ct-btn:disabled { opacity: 0.55; cursor: not-allowed; }

        /* Button shimmer sweep */
        .ct-btn::after {
          content: '';
          position: absolute;
          top: 0; left: -100%;
          width: 60%; height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent);
          transform: skewX(-20deg);
          transition: left 0.5s ease;
        }
        .ct-btn:hover::after { left: 160%; }

        /* Loading spinner */
        .ct-spin {
          width: 16px; height: 16px;
          border: 2px solid rgba(7,9,15,0.3);
          border-top-color: #07090f;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* Sent state */
        .ct-sent-state {
          text-align: center;
          padding: 3rem 1rem;
          animation: fadeUp 0.5s cubic-bezier(0.34,1.56,0.64,1);
        }
        .ct-sent-icon {
          width: 64px; height: 64px;
          border-radius: 50%;
          background: rgba(0,245,212,0.1);
          border: 1.5px solid var(--a);
          display: flex; align-items: center; justify-content: center;
          font-size: 1.8rem;
          color: var(--a);
          margin: 0 auto 1.5rem;
          animation: popIn 0.6s cubic-bezier(0.34,1.56,0.64,1);
          box-shadow: 0 0 40px rgba(0,245,212,0.3);
        }
        @keyframes popIn {
          0%   { transform: scale(0) rotate(-20deg); opacity: 0; }
          70%  { transform: scale(1.15) rotate(3deg); }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        .ct-sent-title {
          font-size: 1.15rem;
          font-weight: 700;
          color: var(--tx);
          margin-bottom: 0.5rem;
        }
        .ct-sent-sub {
          font-family: 'Space Mono', monospace;
          font-size: 0.7rem;
          color: var(--mu);
          letter-spacing: 0.06em;
        }
        .ct-reset {
          margin-top: 1.5rem;
          background: none;
          border: 1px solid var(--bd);
          border-radius: 8px;
          padding: 8px 20px;
          font-family: 'Space Mono', monospace;
          font-size: 0.65rem;
          letter-spacing: 0.1em;
          color: var(--mu);
          cursor: pointer;
          transition: all 0.25s;
        }
        .ct-reset:hover { color: var(--a); border-color: rgba(0,245,212,0.3); }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        @media (max-width: 900px) {
          .ct-grid { grid-template-columns: 1fr; }
          .ct-section { padding: 5rem 1.5rem; }
          .ct-row { grid-template-columns: 1fr; }
        }
        @media (max-width: 480px) {
          .ct-form-inner { padding: 1.75rem 1.25rem; }
        }
      `}</style>

      <section id="contact" className="ct-section">
        {/* Ambient blobs */}
        <div className="ct-blob ct-blob-1" />
        <div className="ct-blob ct-blob-2" />
        <div className="ct-blob ct-blob-3" />
        <canvas ref={canvasRef} className="ct-canvas" />

        <div className="ct-inner">
          {/* Header section */}
          <div className="ct-header ct-reveal-up" ref={headerRef}>
            <div className="ct-eyebrow">
              <span className="ct-eyebrow-line" />
              Let's Connect
              <span className="ct-eyebrow-line right" />
            </div>
            <h2 className="ct-title">
              Get In <span className="ct-title-grad">Touch</span>
            </h2>
            <p className="ct-subtitle">
              Open to new opportunities, collabs, or just a solid tech conversation.
            </p>
          </div>

          <div className="ct-grid">
            {/* Left panel with contact info */}
            <div className="ct-left">
              <div className="ct-avail ct-reveal-left" ref={availRef}>
                <span className="ct-avail-dot" />
                Available for work
              </div>

              {[
                { icon: <FaEnvelope />, label: 'Email', val: <a href="mailto:subo2356@gmail.com">subo2356@gmail.com</a> },
                { icon: <FaPhone />,   label: 'Phone', val: '+91 9851241219' },
                { icon: <FaMapMarkerAlt />, label: 'Location', val: 'India · Available Worldwide' },
              ].map((item, i) => {
                const refs = [card0Ref, card1Ref, card2Ref];
                return (
                  <div
                    className="ct-info-card ct-reveal-left"
                    ref={refs[i]}
                    key={i}
                  >
                    <div className="ct-icard-icon">{item.icon}</div>
                    <div>
                      <div className="ct-icard-label">{item.label}</div>
                      <div className="ct-icard-val">{item.val}</div>
                    </div>
                  </div>
                );
              })}

              <div style={{ marginTop: '4px' }} className="ct-reveal-up" ref={socialsRef}>
                <div style={{
                  fontFamily: "'Space Mono', monospace",
                  fontSize: '0.65rem',
                  letterSpacing: '0.12em',
                  color: 'var(--mu)',
                  textTransform: 'uppercase',
                  marginBottom: '10px'
                }}>
                  Find me on
                </div>
                <div className="ct-socials-row">
                  {[
                    { href: 'https://github.com/SuboKumar2356', icon: <FaGithub /> },
                    { href: 'https://linkedin.com/in/subokumar2356', icon: <FaLinkedin /> },
                    { href: 'https://twitter.com/subokumar2356', icon: <FaTwitter /> },
                  ].map((s, i) => (
                    <a key={i} href={s.href} target="_blank" rel="noopener noreferrer" className="ct-soc">
                      {s.icon}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Form card with contact form */}
            <div className="ct-form-card ct-reveal-right" ref={formRef}>
              {/* Corner accent elements */}
              <div className="ct-corner ct-corner-tl" />
              <div className="ct-corner ct-corner-tr" />
              <div className="ct-corner ct-corner-bl" />
              <div className="ct-corner ct-corner-br" />

              <div className="ct-form-inner">
                {sent ? (
                  <div className="ct-sent-state">
                    <div className="ct-sent-icon"><FaPaperPlane /></div>
                    <div className="ct-sent-title">Message Sent!</div>
                    <div className="ct-sent-sub">I'll get back to you soon. Stay tuned.</div>
                    <button className="ct-reset" onClick={() => setSent(false)}>Send Another →</button>
                  </div>
                ) : (
                  <>
                    <div className="ct-form-title">Send a Message</div>
                    <div className="ct-form-desc"> __All fields required · Usually reply within 24h. </div>

                    {status.message && (
                      <div className={`ct-alert ${status.type}`}>{status.message}</div>
                    )}

                    <form onSubmit={handleSubmit}>
                      <div className="ct-row">
                        {/* Name input field */}
                        <Field
                          label="Your Name"
                          name="name"
                          type="text"
                          value={formData.name}
                          onChange={handleChange}
                          focused={focused}
                          setFocused={setFocused}
                        />
                        {/* Email input field */}
                        <Field
                          label="Email Address"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          focused={focused}
                          setFocused={setFocused}
                        />
                      </div>

                      {/* Subject input field */}
                      <Field
                        label="Subject"
                        name="subject"
                        type="text"
                        value={formData.subject}
                        onChange={handleChange}
                        focused={focused}
                        setFocused={setFocused}
                      />

                      {/* Message textarea field */}
                      <Field
                        label="Message"
                        name="message"
                        type="textarea"
                        value={formData.message}
                        onChange={handleChange}
                        focused={focused}
                        setFocused={setFocused}
                        maxLength={500}
                      />

                      <button type="submit" className="ct-btn" disabled={loading}>
                        {loading ? (
                          <>
                            <div className="ct-spin" />
                            Sending…
                          </>
                        ) : (
                          <>
                            <FaPaperPlane />
                            Send Message
                          </>
                        )}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

/* Reusable Field component for form inputs */
const Field = ({ label, name, type, value, onChange, focused, setFocused, maxLength }) => {
  const isActive = focused === name;
  const isFilled = value.length > 0;
  const isTextarea = type === 'textarea';

  return (
    <div className={`ct-field${isTextarea ? ' textarea' : ''}${isActive ? ' active' : ''}${isFilled ? ' filled' : ''}`}>
      <label className="ct-label">{label}</label>
      {isTextarea ? (
        <>
          <textarea
            className="ct-textarea"
            name={name}
            value={value}
            onChange={onChange}
            onFocus={() => setFocused(name)}
            onBlur={() => setFocused('')}
            required
            maxLength={maxLength}
            rows={5}
          />
          {maxLength && (
            <span className={`ct-counter${value.length > maxLength * 0.85 ? ' warn' : ''}`}>
              {value.length}/{maxLength}
            </span>
          )}
        </>
      ) : (
        <input
          className="ct-input"
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          onFocus={() => setFocused(name)}
          onBlur={() => setFocused('')}
          required
        />
      )}
      <div className="ct-field-line" />
    </div>
  );
};

export default Contact;